import { useEffect, useState } from "react";
import type { SpotView } from "@auto-pools/core";
import { api, ApiError } from "../../api/client";
import { useViewer } from "../../auth/authContext";
import { formatToken, formatWhen } from "../../lib/format";
import { SpotCoinChipsRow } from "./SpotCoinChipsRow";
import { SpotMetricsStrip } from "./SpotMetricsStrip";
import { SpotCardExpanded } from "./SpotCardExpanded";
import {
  spotAvatarCoins,
  spotCardStatus,
  spotCardTitle,
  spotNextSaleAttempt,
  spotOffKey,
  spotOffSubtitle,
  spotShowsPauseButton,
} from "./spotPresentation";

const HIDE_KEY = "auto-pools:spot-card-hidden";

function hidden(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY) === "1";
  } catch {
    return false;
  }
}
function setHidden(value: boolean) {
  try {
    if (value) localStorage.setItem(HIDE_KEY, "1");
    else localStorage.removeItem(HIDE_KEY);
  } catch {
    /* A private window or blocked storage just keeps the card visible every load. */
  }
}

/** A failed icon is asked for again this often: the server may cache it after the card opened. */
const ICON_RETRY_MS = 5 * 60_000;

/**
 * The coin's first letters, covered by its server-cached icon when there is one. A 404 carries
 * `no-store`, so each retry (a fresh `?attempt=`) really asks the server again.
 */
function CoinAvatar({ ticker, muted }: { ticker: string; muted?: boolean }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!failed) return;
    const timer = setTimeout(() => {
      setAttempt((n) => n + 1);
      setFailed(false);
    }, ICON_RETRY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [failed]);
  const src = `/api/spot/coins/${encodeURIComponent(ticker)}/icon${attempt > 0 ? `?attempt=${String(attempt)}` : ""}`;
  return (
    <span className={`spot-coin-avatar${muted ? " muted" : ""}`} aria-hidden="true">
      {ticker.slice(0, 4)}
      {!muted && !failed && (
        <img
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => {
            setFailed(true);
          }}
        />
      )}
    </span>
  );
}

/**
 * A deficit can be booked only from a recent subaccount reading. A running run re-reads it on its
 * own; a paused one is re-read by Resume, a restored one by Start (both read before any order).
 */
function staleReadingText(spot: SpotView): string {
  if (spot.restored === true) return "Start to re-read the subaccount first";
  if (spot.lifecycle === "paused") return "Resume to re-read the subaccount first";
  return "Re-reading the subaccount…";
}

/**
 * The spot leg's own card in My positions (design `50`-`52`, `56`): same anatomy as a pool card —
 * icon stack, title, venue/status/Autopilot chips, one button, chevron, seven-column strip.
 */
export function SpotPositionCard({
  spot,
  expanded,
  onToggle,
  onOpenJournal,
  onOpenSettings,
}: {
  spot: SpotView;
  expanded: boolean;
  onToggle: () => void;
  onOpenJournal?: () => void;
  onOpenSettings?: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(hidden);
  const [confirmingReduction, setConfirmingReduction] = useState(false);
  const viewer = useViewer();

  const status = spotCardStatus(spot);
  const avatars = spotAvatarCoins(spot);
  const title = spotCardTitle(spot);
  const showsStrip = spot.lifecycle !== "off";
  const showsPause = spotShowsPauseButton(spot);
  // The card's own Start (`POST /api/spot/start`, allowed only while an autopilot run exists): a
  // paused run resumes, a restored or never-started one starts. Pools are not touched.
  const startLabel = spot.actions.resume.allowed ? "Resume spot" : "Start spot";
  const keyMissing = spot.key.active === null;
  const offKey = spotOffKey(spot);
  const nextTry = spotNextSaleAttempt(spot);
  // No run-start timestamp exists on `SpotView` (unlike `AutopilotStatus.startedAt`), so the meta
  // line reports the next scheduled review instead of guessing a start time from the key's own.
  const nextReview =
    spot.slices.length === 0
      ? null
      : spot.slices.reduce<string | null>((earliest, slice) => {
          if (earliest === null) return slice.nextReviewAt;
          return Date.parse(slice.nextReviewAt) < Date.parse(earliest)
            ? slice.nextReviewAt
            : earliest;
        }, null);

  /** One card command (`/api/spot/*`); the SSE `spot` event reloads the view afterwards. */
  async function command(path: string, body: Record<string, unknown>, refused: string) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api<SpotView>(path, {
        method: "POST",
        body: { ...body, idempotencyKey: crypto.randomUUID() },
        sensitive: true,
      });
      setConfirmingReduction(false);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : refused);
    } finally {
      setPending(false);
    }
  }
  const pauseAndSell = () => command("/spot/pause", { mode: "sell" }, "Pause was not accepted");
  const startSpot = () => command("/spot/start", {}, "Start was not accepted");
  const bookReduction = () =>
    command("/spot/books/confirm-reduction", {}, "The reduction was not booked");

  if (spot.lifecycle === "off" && dismissed) return null;

  return (
    <article
      // No bare `position-card` token: `tests/e2e/terminal.spec.ts` locates "the" position card
      // by that class alone and must keep resolving to exactly one real pool/wallet position, not
      // this one too (styles.css gives `.spot-position-card` the same chrome/touch-target rules).
      className={`compact-position-card spot-position-card ${expanded ? "expanded" : ""}`}
    >
      <header className="position-card-header">
        <div className="spot-avatar-stack" aria-hidden="true">
          {avatars.shown.length === 0 ? (
            <CoinAvatar ticker="—" muted />
          ) : (
            avatars.shown.map((ticker) => <CoinAvatar key={ticker} ticker={ticker} />)
          )}
          {avatars.overflow > 0 && (
            <span className="spot-coin-avatar spot-coin-avatar-more">+{avatars.overflow}</span>
          )}
        </div>
        <div className="position-identity-copy">
          <div className="tag-line">
            <h2>{title}</h2>
            <span className="tag purple spot-venue-chip">Bybit</span>
            <span
              className={`tag lifecycle ${status.tone === "good" ? "green" : status.tone === "bad" ? "red" : status.tone === "warn" ? "amber" : "muted"}`}
            >
              <i
                className={`status-dot ${status.tone === "good" ? "good" : status.tone === "bad" ? "bad" : status.tone === "warn" ? "warn" : ""}`}
              />
              {status.label}
            </span>
            {spot.lifecycle !== "off" && <span className="tag autopilot-chip">Autopilot</span>}
          </div>
          {/* `spot-position-meta`, not the bare `.position-meta` real position cards share: see
              the className comment on the <article> above for why. */}
          <div className="spot-position-meta">
            {spot.key.active && (
              <>
                <span className="num">subaccount {spot.key.active.userId}</span>
                <span aria-hidden="true">·</span>
              </>
            )}
            {spot.lifecycle === "off" ? (
              <span>{spotOffSubtitle(spot)}</span>
            ) : spot.readingSubaccount ? (
              <span>reading the subaccount…</span>
            ) : nextReview !== null ? (
              <span>next review {formatWhen(nextReview)}</span>
            ) : (
              <span>managed by the autopilot</span>
            )}
          </div>
        </div>
        <div className="position-actions">
          {!viewer && spot.actions.start.allowed && (
            <button
              type="button"
              className="button primary"
              disabled={pending}
              onClick={() => void startSpot()}
            >
              {pending ? "Starting…" : startLabel}
            </button>
          )}
          {!viewer && spot.lifecycle === "off" && keyMissing && offKey !== "vault-locked" && (
            <button type="button" className="button primary" onClick={onOpenSettings}>
              Add a Bybit key
            </button>
          )}
          {spot.lifecycle === "off" && (
            <button
              type="button"
              className="button ghost small"
              onClick={() => {
                setHidden(true);
                setDismissed(true);
              }}
            >
              Hide this card
            </button>
          )}
          {!viewer && showsPause && (
            <button
              type="button"
              className="button danger"
              disabled={pending}
              onClick={() => void pauseAndSell()}
            >
              {pending ? "Pausing…" : "Pause and sell → USDT"}
            </button>
          )}
          {showsStrip && (
            <button
              type="button"
              className="icon-button expand-position"
              aria-label={expanded ? "Collapse position" : "Expand position"}
              aria-expanded={expanded}
              onClick={onToggle}
            >
              <svg viewBox="0 0 14 14" aria-hidden="true">
                <path d="m3.5 5.5 3.5 3.5 3.5-3.5" />
              </svg>
            </button>
          )}
        </div>
      </header>
      {error && (
        <p className="inline-alert warning" role="alert">
          {error}
        </p>
      )}
      {spot.state === "needs-attention" &&
        (spot.reason === "sale-failing" || spot.reason === "key-broken-holding") && (
          <p className="inline-alert error spot-attention-banner" role="alert">
            {spot.reason === "sale-failing"
              ? "BTC closed below its average and the bot must sell, but the sale keeps failing."
              : "The Bybit key stopped working while coins are held."}
            {spot.saleFailing && (
              <span className="num spot-attention-attempt">
                {" "}
                attempt {String(spot.saleFailing.attempts)}
                {nextTry !== null && ` · next try ${formatWhen(nextTry)}`}
              </span>
            )}
            {onOpenJournal && (
              <button type="button" className="button ghost small" onClick={onOpenJournal}>
                Open journal →
              </button>
            )}
          </p>
        )}
      {spot.booksMismatch && (
        <div className="inline-alert warning spot-books-mismatch" role="alert">
          <p>
            Balances on the subaccount no longer match the books, so the engine places no orders.
            {spot.booksMismatch.cause === "deficit"
              ? " Something left the subaccount; book the reduction to resume trading."
              : " The books are inconsistent; see the journal."}
          </p>
          {spot.actions.confirmReduction.reasonCode === "SPOT_REDUCTION_STALE" ? (
            <p className="spot-reduction-stale">{staleReadingText(spot)}</p>
          ) : viewer ? null : !confirmingReduction ? (
            <button
              type="button"
              className="button small"
              disabled={!spot.actions.confirmReduction.allowed || pending}
              onClick={() => {
                setConfirmingReduction(true);
              }}
            >
              Book the reduction
            </button>
          ) : (
            <div role="group" aria-label="Book the reduction" className="spot-reduction-confirm">
              <p>
                This removes from the books what is no longer on the subaccount:{" "}
                {[
                  ...(spot.booksMismatch.usdtShortfall === null
                    ? []
                    : [`${formatToken(spot.booksMismatch.usdtShortfall)} USDT`]),
                  ...Object.entries(spot.booksMismatch.coins).map(
                    ([coin, qty]) => `${formatToken(qty)} ${coin}`,
                  ),
                ].join(", ")}
                , taken from the slices that held it, pro rata. Trading resumes afterwards.
              </p>
              <button
                type="button"
                className="button danger small"
                disabled={pending || !spot.actions.confirmReduction.allowed}
                onClick={() => void bookReduction()}
              >
                Confirm the reduction
              </button>
              <button
                type="button"
                className="button ghost small"
                disabled={pending}
                onClick={() => {
                  setConfirmingReduction(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
      {showsStrip && <SpotCoinChipsRow spot={spot} />}
      {showsStrip && <SpotMetricsStrip spot={spot} />}
      {expanded && showsStrip && <SpotCardExpanded spot={spot} onOpenJournal={onOpenJournal} />}
    </article>
  );
}
